'use client';

import { Card } from '@/components/ui';
import { ScrollableContainer } from '../common/scrollable-container';
import { DistributionGraph } from '../common/distribution-graph';
import jStat from 'jstat';

export interface ConfidenceIntervalResult {
  mean: number;
  standardError: number;
  confidenceLevel: number;
  sampleSize: number;
  lowerBound: number;
  upperBound: number;
  data: number[];
}

interface ConfidenceIntervalCalculatorProps {
  visualizationData: ConfidenceIntervalResult | null;
}

export function ConfidenceIntervalCalculator({ visualizationData: result }: ConfidenceIntervalCalculatorProps) {
  if (!result) {
    return null;
  }

  const { mean, standardError, confidenceLevel, sampleSize, lowerBound, upperBound, data } = result;
  const marginOfError = upperBound - mean;

  // Generate normal distribution points for visualization
  const numPoints = 100;
  const range = standardError * 4;
  const points = Array.from({ length: numPoints }, (_, i) => {
    const x = mean - range + (2 * range * i) / (numPoints - 1);
    return jStat.normal.pdf(x, mean, standardError);
  });

  // Generate x-axis values
  const xValues = Array.from({ length: numPoints }, (_, i) => mean - range + (2 * range * i) / (numPoints - 1));

  return (
    <ScrollableContainer>
      <div className="space-y-6">
        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Confidence interval results</h3>
          <div className="space-y-4">
            <div>
              <p className="font-medium">Sample mean:</p>
              <p>{mean.toFixed(4)}</p>
            </div>
            <div>
              <p className="font-medium">Standard error:</p>
              <p>{standardError.toFixed(4)}</p>
            </div>
            <div>
              <p className="font-medium">Confidence level:</p>
              <p>{(confidenceLevel * 100).toFixed(1)}%</p>
            </div>
            <div>
              <p className="font-medium">Sample size:</p>
              <p>{sampleSize}</p>
            </div>
            <div>
              <p className="font-medium">Confidence interval:</p>
              <p>
                {mean.toFixed(4)} ± {marginOfError.toFixed(4)} 
                <br />
                ({lowerBound.toFixed(4)} to {upperBound.toFixed(4)})
              </p>
            </div>
            <div>
              <p className="font-medium">Interpretation:</p>
              <p>
                We are {(confidenceLevel * 100).toFixed(1)}% confident that the true population mean 
                falls between {lowerBound.toFixed(4)} and {upperBound.toFixed(4)}.
              </p>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <h3 className="text-lg font-semibold mb-4">Sampling distribution</h3>
          <DistributionGraph
            data={points}
            xAxisValues={xValues}
            title="Sampling Distribution of the Mean"
            xAxisLabel="Value"
            yAxisLabel="Probability density"
            height={300}
            labels={[
              {
                value: mean,
                text: `Mean = ${mean.toFixed(4)}`,
                color: '#4d79ff',
                lineStyle: 'solid'
              },
              {
                value: lowerBound,
                text: `Lower bound = ${lowerBound.toFixed(4)}`,
                color: '#ff4d4d',
                lineStyle: 'dashed'
              },
              {
                value: upperBound,
                text: `Upper bound = ${upperBound.toFixed(4)}`,
                color: '#ff4d4d',
                lineStyle: 'dashed'
              }
            ]}
          />
        </Card>
      </div>
    </ScrollableContainer>
  );
} 